import { AuthUserPermission, Can, useOrgUser } from '@cibo/profile'
import { Breadcrumbs, Container, Link, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Outlet, Link as RRLink, generatePath, useLocation, useParams } from 'react-router-dom'

import { useProgramContent } from '@cibo/programs'
import { SaveIndicator, useParamAsInteger } from '@cibo/ui'
import { SaveIndicatorProvider } from '@cibo/ui/src/components/SaveIndicator/SaveIndicatorProvider'
import { useCallback, useEffect } from 'react'
import { ProgramEngagementProvider } from '../../features/ProgramEngagement'
import {
  GROWERS_PATHS,
  GROWERS_ROUTES,
  GrowersRouteParams,
} from '../../navigation/types/growersRoutes'
import { Forbidden } from '../Forbidden'

export const GrowersLayout = () => {
  const location = useLocation()
  const allPaths = location.pathname.split('/')
  // only breadcrumb up to the engagement name
  const paths = allPaths.slice(1, 4)
  const { t } = useTranslation('impact/pages/GrowersLayout')

  const { programId } = useParams<GrowersRouteParams>()
  const userId = useParamAsInteger('userId')

  const user = useOrgUser(userId)
  const program = useProgramContent(programId)

  useEffect(() => window.scrollTo(0, 0), [location.pathname])

  const pathLabel = useCallback(
    (path: string) => {
      if (path === userId?.toString()) {
        if (user.data?.displayNameFull) {
          return user.data.displayNameFull
        }
        return t('loading')
      }
      if (path === programId) {
        if (program.data?.content?.fields?.title) {
          return program.data.content.fields.title
        }
        return t('loading')
      }
      return t(`path_${path}`)
    },
    [user.dataUpdatedAt, userId, program.dataUpdatedAt, programId]
  )

  if (allPaths.includes(GROWERS_PATHS.ADD_FIELD)) {
    return <Outlet />
  }

  return (
    <Can useAny={[AuthUserPermission.GROWERS_MODULE]} fallback={<Forbidden />}>
      <SaveIndicatorProvider>
        <ProgramEngagementProvider
          basePath={generatePath(GROWERS_ROUTES.PROGRAM, {
            userId: !!userId ? `${userId}` : '',
            programId: programId ?? '',
          })}
          programId={programId ?? ''}
          userId={userId || Number.NaN}
        >
          <Container>
            <Stack flex={1}>
              {paths.length > 1 && (
                <Stack direction="row" justifyContent={'space-between'} spacing={3} px={3} py={2.5}>
                  <Breadcrumbs>
                    {paths.map((path, index) =>
                      index === paths.length - 1 ? (
                        <Typography color="text.primary" key={index}>
                          {pathLabel(path)}
                        </Typography>
                      ) : (
                        <Link
                          key={index}
                          component={RRLink}
                          //@todo figure out why relative routing appears to be broken in 6.2.x
                          to={`/${paths.slice(0, index + 1).join('/')}`}
                          underline="hover"
                          color="text.secondary"
                        >
                          {pathLabel(path)}
                        </Link>
                      )
                    )}
                  </Breadcrumbs>
                  <SaveIndicator />
                </Stack>
              )}
              <Outlet />
            </Stack>
          </Container>
        </ProgramEngagementProvider>
      </SaveIndicatorProvider>
    </Can>
  )
}
